import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Phone, ArrowLeft, SkipForward, ChevronRight, ExternalLink, Loader2, MapPin, History } from "lucide-react";
import { toast } from "sonner";

const UITKOMST_LABEL = {
  geen_gehoor: "Geen gehoor", later_terugbellen: "Later terugbellen",
  sample: "Sample opsturen", deal: "Deal!",
};

const FASE = {
  "Cold": { dot: "bg-slate-400", bg: "bg-slate-100", tx: "text-slate-700" },
  "Terugbellen": { dot: "bg-amber-500", bg: "bg-amber-50", tx: "text-amber-900" },
  "Sample opsturen": { dot: "bg-sky-500", bg: "bg-sky-50", tx: "text-sky-900" },
  "Sample opgestuurd": { dot: "bg-indigo-500", bg: "bg-indigo-50", tx: "text-indigo-900" },
  "Onderhandeling": { dot: "bg-violet-500", bg: "bg-violet-50", tx: "text-violet-900" },
  "Deal!": { dot: "bg-emerald-500", bg: "bg-emerald-50", tx: "text-emerald-900" },
  "Beltaak": { dot: "bg-cyan-600", bg: "bg-cyan-50", tx: "text-cyan-900" },
};
const PALET = [
  { dot: "bg-teal-500", bg: "bg-teal-50", tx: "text-teal-900" },
  { dot: "bg-orange-500", bg: "bg-orange-50", tx: "text-orange-900" },
  { dot: "bg-blue-500", bg: "bg-blue-50", tx: "text-blue-900" },
  { dot: "bg-pink-500", bg: "bg-pink-50", tx: "text-pink-900" },
  { dot: "bg-stone-500", bg: "bg-stone-100", tx: "text-stone-800" },
];
const _hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
const fc = (f) => FASE[f] || PALET[_hash(f || "") % PALET.length];
const telVan = (k) => (k ? (k.telefoon || k.contact?.mobiel || k.contact?.telefoon || "") : "").trim();
const SCOPES = [{ k: "vandaag", l: "Vandaag" }, { k: "achterstallig", l: "Achterstallig" }, { k: "ongepland", l: "Ongepland" }, { k: "alles", l: "Alles" }];
const SCOPE_LABEL = { vandaag: "'vandaag'", achterstallig: "'achterstallig'", ongepland: "'ongepland'", alles: "deze selectie" };

export default function Belkansen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fases = params.get("fases") || "";
  const [scope, setScope] = useState(params.get("scope") || "vandaag");
  const [kansen, setKansen] = useState([]);
  const [idx, setIdx] = useState(0);
  const [notitie, setNotitie] = useState("");
  const [terugbellen, setTerugbellen] = useState("");
  const [busy, setBusy] = useState(false);
  const [klaar, setKlaar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [btUitkomsten, setBtUitkomsten] = useState([]);
  const telRef = useRef(null);

  const laad = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/verkoopkansen/belsessie", { params: { fases, scope } });
      setKansen(data.kansen || []); setIdx(0); setKlaar(0);
    } catch { toast.error("Belsessie laden mislukt"); }
    finally { setLoading(false); }
  }, [fases, scope]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => {
    api.get("/instellingen/campagnes")
      .then((r) => setBtUitkomsten((r.data?.uitkomsten || []).filter((u) => u.code !== "niet_bereikbaar")))
      .catch(() => {});
  }, []);

  const huidige = kansen[idx];
  const isBel = huidige?.type === "beltaak";
  const volgende = useCallback(() => { setNotitie(""); setTerugbellen(""); setIdx((i) => i + 1); }, []);

  const log = useCallback(async (uitkomst) => {
    if (!huidige || busy) return;
    if (uitkomst === "later_terugbellen" && !terugbellen) { toast.error("Kies een terugbel-moment"); return; }
    setBusy(true);
    try {
      const body = { uitkomst, notitie };
      if (terugbellen && (uitkomst === "later_terugbellen" || uitkomst === "geen_gehoor")) body.gepland_op = new Date(terugbellen).toISOString();
      await api.post(`/verkoopkansen/${huidige.id}/belletjes`, body);
      toast.success(`Opgeslagen: ${UITKOMST_LABEL[uitkomst]}`);
      setKlaar((k) => k + 1); volgende();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setBusy(false); }
  }, [huidige, busy, notitie, terugbellen, volgende]);

  const logBeltaak = useCallback(async (code) => {
    if (!huidige || busy) return;
    if (code === "terugbellen" && !terugbellen) { toast.error("Kies een terugbel-moment"); return; }
    setBusy(true);
    try {
      await api.post(`/beltaken/${huidige.id}/uitkomst`, { uitkomst: code, notitie, terugbellen_op: terugbellen ? new Date(terugbellen).toISOString() : null });
      toast.success(`Opgeslagen: ${btUitkomsten.find((u) => u.code === code)?.label || code}`);
      setKlaar((k) => k + 1); volgende();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setBusy(false); }
  }, [huidige, busy, notitie, terugbellen, btUitkomsten, volgende]);

  const bel = useCallback(() => {
    const nr = telVan(huidige);
    if (!nr) { toast.message("Geen telefoonnummer bekend"); return; }
    if (telRef.current) { telRef.current.click(); return; }
    window.location.href = `tel:${nr.replace(/\s/g, "")}`;
  }, [huidige]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "F1") { e.preventDefault(); e.stopPropagation(); bel(); return; }
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key.toLowerCase() === "s") { volgende(); return; }
      if (isBel) {
        const n = parseInt(e.key, 10);
        if (n >= 1 && n <= btUitkomsten.length) logBeltaak(btUitkomsten[n - 1].code);
        return;
      }
      if (e.key === "1") log("geen_gehoor");
      else if (e.key === "2") log("later_terugbellen");
      else if (e.key === "3") log("sample");
      else if (e.key === "4") log("deal");
    };
    window.addEventListener("keydown", h, { capture: true });
    return () => window.removeEventListener("keydown", h, { capture: true });
  }, [log, logBeltaak, volgende, bel, isBel, btUitkomsten]);

  const ScopeSwitch = () => (
    <div className="flex items-center justify-center">
      <div className="inline-flex items-center gap-1 rounded-lg bg-secondary p-1" data-testid="belkansen-scope-switch">
        {SCOPES.map((s) => (
          <button key={s.k} onClick={() => setScope(s.k)} data-testid={`belkansen-scope-${s.k}`}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${scope === s.k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{s.l}</button>
        ))}
      </div>
    </div>
  );

  const fasesTekst = fases ? fases.split(",").join(" · ") : "alle te bellen";

  if (loading) return <div className="max-w-xl mx-auto text-center py-16 text-muted-foreground flex items-center justify-center gap-2" data-testid="belkansen-laden"><Loader2 className="h-5 w-5 animate-spin" />Belsessie laden…</div>;

  if (kansen.length === 0) return (
    <div className="max-w-xl mx-auto text-center py-16 space-y-4" data-testid="belkansen-leeg">
      <ScopeSwitch />
      <p className="text-muted-foreground">Geen verkoopkansen om te bellen in {SCOPE_LABEL[scope] || "deze selectie"} ({fasesTekst}). Verplaats achterstallige kansen naar vandaag, of kies hierboven een andere periode.</p>
      <Button variant="outline" onClick={() => navigate("/agenda")} data-testid="belkansen-terug"><ArrowLeft className="h-4 w-4 mr-1" />Naar agenda</Button>
    </div>
  );

  if (!huidige) return (
    <div className="max-w-xl mx-auto text-center py-16 space-y-3" data-testid="belkansen-klaar">
      <h2 className="text-xl font-semibold">Klaar — {klaar} van {kansen.length} afgehandeld</h2>
      <Button onClick={() => navigate("/agenda")}><ArrowLeft className="h-4 w-4 mr-1" />Naar agenda</Button>
    </div>
  );

  const telNummer = telVan(huidige);
  const c = fc(huidige.fase);

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="belkansen">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/agenda")} data-testid="belkansen-terug"><ArrowLeft className="h-4 w-4 mr-1" />Agenda</Button>
        <span className="text-sm text-muted-foreground" data-testid="belkansen-voortgang">{idx + 1} van {kansen.length}</span>
        <Button variant="ghost" size="sm" onClick={volgende} data-testid="belkansen-overslaan"><SkipForward className="h-4 w-4 mr-1" />Overslaan (S)</Button>
      </div>
      <div className="text-center text-xs text-muted-foreground" data-testid="belkansen-scope">Belsessie: <strong>{fasesTekst}</strong></div>
      <ScopeSwitch />

      <div className="bg-card border border-border rounded-2xl p-5 space-y-4 shadow-sm">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${c.bg} ${c.tx}`} data-testid="belkansen-fase"><span className={`h-2 w-2 rounded-full ${c.dot}`} />{huidige.fase}</span>
            {huidige.klantcode && <span>· {huidige.klantcode}</span>}
          </div>
          <h1 className="text-2xl font-semibold leading-tight mt-1">
            <Link to={`/organisaties/${huidige.organisatie_id}`} className="hover:text-primary" data-testid="belkansen-org">{huidige.organisatie || "Onbekende organisatie"}</Link>
          </h1>
          <div className="text-sm text-muted-foreground">
            {huidige.contact?.naam ? `${huidige.contact.naam}${huidige.contact.functie ? ` · ${huidige.contact.functie}` : ""}` : "geen contactpersoon"}
            {(huidige.plaats || huidige.land) && <span className="inline-flex items-center gap-1 ml-1"><MapPin className="h-3 w-3" />{[huidige.plaats, huidige.land].filter(Boolean).join(", ")}</span>}
          </div>
          {telNummer ? (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <a ref={telRef} href={`tel:${telNummer.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-2xl font-semibold text-primary tabular-nums" data-testid="belkansen-telefoon"><Phone className="h-6 w-6" />{telNummer}</a>
              <Button size="sm" onClick={bel} data-testid="belkansen-bel-knop"><Phone className="h-4 w-4 mr-1" />Bellen <kbd className="ml-1.5 rounded bg-primary-foreground/20 px-1 text-[10px] font-sans">F1</kbd></Button>
            </div>
          ) : <div className="mt-2 text-sm text-muted-foreground" data-testid="belkansen-geen-nummer">Geen telefoonnummer bekend — log de uitkomst handmatig</div>}
          <p className="mt-2 text-sm bg-amber-50 text-amber-900 border border-amber-200 rounded-lg px-3 py-2" data-testid="belkansen-kans"><strong>{isBel ? "Taak" : "Kans"}:</strong> {huidige.naam || "—"}{!isBel && huidige.bedrag ? ` · ${fmtEUR(huidige.bedrag)}` : ""}</p>
          {huidige.aantal_belletjes > 0 && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1" data-testid="belkansen-historie">
              <History className="h-3 w-3" />{huidige.aantal_belletjes} eerdere belpoging(en){huidige.laatste_uitkomst ? ` · laatst: ${UITKOMST_LABEL[huidige.laatste_uitkomst] || huidige.laatste_uitkomst}` : ""}
            </div>
          )}
          {isBel
            ? <Link to={`/bellen/modus?taak=${huidige.id}`} className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="belkansen-open-kans"><ExternalLink className="h-3 w-3" />Open belkaart</Link>
            : <Link to={`/verkoopkansen/${huidige.id}`} className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="belkansen-open-kans"><ExternalLink className="h-3 w-3" />Open volledige kans</Link>}
        </div>

        <div className="space-y-2">
          <Textarea rows={2} placeholder="Notitie…" value={notitie} onChange={(e) => setNotitie(e.target.value)} data-testid="belkansen-notitie" />
          <div className="flex items-center gap-2 text-xs"><span className="text-muted-foreground">Terugbellen op:</span><Input type="datetime-local" className="h-8 w-56" value={terugbellen} onChange={(e) => setTerugbellen(e.target.value)} data-testid="belkansen-terugbellen" /></div>
          {isBel ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="belkansen-uitkomsten">
              {btUitkomsten.map((u, i) => (
                <Button key={u.code} variant={u.code === "bereikt_positief" ? "default" : "outline"} className="h-auto py-2 flex-col items-start text-left" disabled={busy} onClick={() => logBeltaak(u.code)} data-testid={`uitkomst-${u.code}`}>
                  <span className="text-[10px] opacity-60">{i + 1}</span><span className="text-xs font-medium leading-tight">{u.label}</span>
                </Button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="belkansen-uitkomsten">
              <Button variant="outline" className="h-auto py-2.5" disabled={busy} onClick={() => log("geen_gehoor")} data-testid="uitkomst-geen_gehoor"><span className="text-[10px] opacity-60 mr-1">1</span>Geen gehoor</Button>
              <Button variant="outline" className="h-auto py-2.5" disabled={busy} onClick={() => log("later_terugbellen")} data-testid="uitkomst-later_terugbellen"><span className="text-[10px] opacity-60 mr-1">2</span>Later terugbellen</Button>
              <Button variant="outline" className="h-auto py-2.5" disabled={busy} onClick={() => log("sample")} data-testid="uitkomst-sample"><span className="text-[10px] opacity-60 mr-1">3</span>Sample opsturen</Button>
              <Button className="h-auto py-2.5" disabled={busy} onClick={() => log("deal")} data-testid="uitkomst-deal"><span className="text-[10px] opacity-60 mr-1">4</span>Deal!</Button>
            </div>
          )}
        </div>
      </div>
      <div className="text-center text-xs text-muted-foreground">Sneltoetsen: <strong>F1</strong> bellen · 1–{isBel ? (btUitkomsten.length || 1) : 4} uitkomst · S overslaan <ChevronRight className="h-3 w-3 inline" /></div>
    </div>
  );
}
